// Ask Helix drill contract — DRILLING IS ASKING.
//
// A drill is not an endpoint: clicking a governed number or card on the canvas emits a
// canonical follow-up question through the existing conversation API (message +
// conversationId). The server resolves referents from the conversation context frame
// (#1767) and routes the question through the same deterministic seam router as a typed
// question, so every drill inherits the full governance stack (routing, fences,
// provenance, grader, closed component registry) with zero bespoke drill surface.
//
// The verb vocabulary is CLOSED. Each verb maps to a canonical question template that is
// pinned to the answering seam's own intent matrix in
// apps/api/src/ask-helix/<seam>/*-intent.ts, so routing is regression-tested server-side
// before the UI ever emits it:
// - 'provenance'     -> provenance drilldown seam (#1870). Template carries the
//                        "where does … come from" cue plus the word "number"
//                        (a drillable-identity cue), and interpolates the clicked
//                        element's label so the spec parser can resolve the identity.
// - 'drift_recovery' -> drift realization seam. Template carries the "recover" cue plus
//                        the word "commitment" (a recover-target cue), routing to the
//                        console-proven realization/recovery answer.
//
// Where the emitting seam already proposes its own follow-up (e.g. decision-package lever
// rows carry a per-row `drilldownQuestion`), that server-proposed question wins over the
// client template — set it on `question` and the template is bypassed.

export type AskHelixDrillVerb = 'provenance' | 'drift_recovery';

export type AskHelixDrillTarget = {
  /** Which closed drill verb the click fires. */
  verb: AskHelixDrillVerb;
  /**
   * Human label of the clicked element (e.g. the KPI label, the realization metric
   * label). Interpolated into the canonical question so the answering seam's spec
   * parser can resolve the identity from the question text — the governed channel.
   */
  label: string;
  /**
   * Placeholder of the governed numeric binding behind the clicked figure, when the
   * element renders one. Shares the referent id-space of the conversation context
   * frame's numericBindingRefs (bindingId = valuePath || placeholder || evidence id).
   * Travels for client-side turn attribution; the wire referent itself is resolved
   * server-side from conversationId.
   */
  bindingPlaceholder?: string | null;
  /** componentId of the emitting canvas component, for turn origin attribution. */
  componentId?: string | null;
  /** componentType of the emitting canvas component, for turn origin attribution. */
  componentType?: string | null;
  /** Row/item id inside the component (e.g. a waterfall row id), when applicable. */
  itemId?: string | null;
  /**
   * Server-proposed follow-up question. When present it is relayed verbatim instead of
   * the client template (the API is the source of truth for its own follow-ups).
   */
  question?: string | null;
};

/**
 * Build the canonical follow-up question for a drill target. Server-proposed questions
 * win; otherwise the closed per-verb template is interpolated with the clicked label.
 */
export function buildAskHelixDrillQuestion(target: AskHelixDrillTarget): string {
  const proposed = typeof target.question === 'string' ? target.question.trim() : '';
  if (proposed) {
    return proposed;
  }

  const label = target.label.trim();
  switch (target.verb) {
    case 'provenance':
      return label
        ? `Where does the ${label} number come from?`
        : 'Where does this number come from?';
    case 'drift_recovery':
      return label
        ? `How do we recover the ${label} commitment?`
        : 'How do we recover this commitment?';
  }
}
